import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Flag, Check, Share2, Siren, Instagram } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const RotatingStamp = () => (
  <div className="relative w-28 h-28 lg:w-32 lg:h-32 shrink-0" aria-hidden="true">
    <svg viewBox="0 0 100 100" className="w-full h-full animate-spin-slow">
      <defs>
        <path id="stamp-circle" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
      </defs>
      <text className="fill-[#F4C95D]" style={{ fontSize: "9.2px", letterSpacing: "1.8px", fontFamily: "Manrope, sans-serif", fontWeight: 700 }}>
        <textPath href="#stamp-circle">UPDATED WEEKLY • CYBERSECYYC • FRESH SCAM INTEL •</textPath>
      </text>
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <Siren className="w-7 h-7 text-[#F4C95D]" strokeWidth={1.8} />
    </div>
  </div>
);

const SneakMeter = ({ level }) => (
  <div className="flex items-center gap-3" data-testid="scam-sneak-meter">
    <span className="text-xs font-bold tracking-widest uppercase text-[#FDFBF7]/60">Sneakiness</span>
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`w-3 h-3 rounded-full ${n <= level ? "bg-[#D96C4A]" : "bg-[#FDFBF7]/15"}`}
        />
      ))}
    </div>
    <span className="font-hand text-xl text-[#F4C95D] -rotate-2">
      {level >= 4 ? "very sneaky!" : level === 3 ? "pretty sneaky" : "mildly sneaky"}
    </span>
  </div>
);

export default function ScamOfWeek() {
  const [scam, setScam] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/scam-of-week`).then((res) => setScam(res.data)).catch(() => {});
  }, []);

  const handleShare = async () => {
    const shareData = {
      title: "Scam Of The Week — CyberSecYYC",
      text: scam ? `Heads up: ${scam.title}. ${scam.hook}` : "Check this week's scam alert for Calgary.",
      url: `${window.location.origin}/#scam-of-week`,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* dismissed */ }
    } else {
      await navigator.clipboard.writeText(shareData.url);
      toast.success("Link copied — send it to someone you love!");
    }
  };

  if (!scam) return null;

  return (
    <section id="scam-of-week" className="relative bg-[#33638B] text-[#FDFBF7] scroll-mt-24 overflow-hidden" data-testid="scam-of-week-section">
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <span className="absolute -top-24 -right-8 font-display font-semibold text-[22rem] leading-none text-[#F4C95D]/10 rotate-12">g</span>
        <span className="absolute -top-10 right-40 font-display font-semibold text-[16rem] leading-none text-[#D96C4A]/20 -rotate-[20deg]">S</span>
        <span className="absolute -bottom-44 -left-16 font-display font-semibold text-[26rem] leading-none text-[#FDFBF7]/[0.05] -rotate-6">y</span>
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap items-end justify-between gap-8 mb-14 lg:mb-20"
        >
          <div>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#F4C95D] mb-4" data-testid="scam-of-week-eyebrow">
              No. 01 — Weekly Field Notes
            </p>
            <h2 className="font-display font-semibold text-4xl/[1.1] sm:text-5xl/[1.1] lg:text-6xl/[1.1] tracking-tight" data-testid="scam-of-week-heading">
              Scam of <span className="text-[#D96C4A]">the week</span>
            </h2>
            <p className="mt-4 text-[#FDFBF7]/70 max-w-md text-sm md:text-base leading-relaxed">
              One fresh scam, decoded in plain English every week — so you can spot it before it spots you.
            </p>
          </div>
          <RotatingStamp />
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65 }}
            className="lg:col-span-3"
          >
            <p className="font-hand text-2xl text-[#F4C95D] mb-3" data-testid="scam-week-of">{scam.week_of}</p>
            <h3 className="font-display font-semibold text-3xl/[1.15] sm:text-4xl/[1.15] lg:text-[3.3rem]/[1.15] tracking-tight mb-6" data-testid="scam-title">
              {scam.title}
            </h3>
            <p className="text-lg md:text-xl text-[#FDFBF7]/90 leading-relaxed mb-6 border-l-4 border-[#D96C4A] pl-5" data-testid="scam-hook">
              {scam.hook}
            </p>
            <p className="text-[#FDFBF7]/70 leading-relaxed mb-8 max-w-2xl" data-testid="scam-description">
              {scam.description}
            </p>
            <SneakMeter level={scam.severity} />

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={handleShare}
                data-testid="scam-share-button"
                className="inline-flex items-center gap-2 bg-[#D96C4A] text-white font-display font-medium px-7 py-3.5 rounded-full hover:scale-[1.03] transition-transform duration-200"
              >
                <Share2 className="w-4 h-4" strokeWidth={2} />
                Warn someone you love
              </button>
              <a
                href="https://www.instagram.com/cybersecyyc/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="scam-instagram-link"
                className="inline-flex items-center gap-2 border border-[#FDFBF7]/30 text-[#FDFBF7] font-display font-medium px-7 py-3.5 rounded-full hover:bg-[#FDFBF7] hover:text-[#33638B] transition-colors duration-200"
              >
                <Instagram className="w-4 h-4" strokeWidth={2} />
                More alerts on IG
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, delay: 0.12 }}
            className="lg:col-span-2 space-y-5"
          >
            <div className="bg-[#FDFBF7] text-[#33638B] rounded-3xl p-8 rotate-[0.6deg]" data-testid="scam-red-flags-card">
              <h4 className="font-display font-semibold text-xl mb-5 flex items-center gap-2.5">
                <span className="bg-[#D96C4A]/10 rounded-full p-2"><Flag className="w-4 h-4 text-[#D96C4A]" strokeWidth={2} /></span>
                How to spot it
              </h4>
              <ul className="space-y-3.5">
                {scam.red_flags.map((flag, i) => (
                  <li key={i} className="flex gap-3 text-sm leading-relaxed" data-testid={`scam-red-flag-${i}`}>
                    <span className="font-display font-semibold text-[#D96C4A] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[#33638B]/80">{flag}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#F4C95D] text-[#33638B] rounded-3xl p-8 rotate-[-0.6deg]" data-testid="scam-what-to-do-card">
              <h4 className="font-display font-semibold text-xl mb-5 flex items-center gap-2.5">
                <span className="bg-[#33638B]/10 rounded-full p-2"><Check className="w-4 h-4 text-[#33638B]" strokeWidth={2.5} /></span>
                What to do instead
              </h4>
              <ul className="space-y-3.5">
                {scam.what_to_do.map((step, i) => (
                  <li key={i} className="flex gap-3 text-sm leading-relaxed" data-testid={`scam-what-to-do-${i}`}>
                    <Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5} />
                    <span className="text-[#33638B]/85">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
